import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Landmark, CreditCard, Eye, EyeOff, Trash2, ShieldCheck } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const input = "w-full px-3 py-2.5 rounded-xl border text-sm";

const group4 = (v) => (v && !v.includes("@") ? v.replace(/(.{4})/g, "$1 ").trim() : v);

function SavedRow({ icon: Icon, label, masked, sub, full, onReveal, onEdit, onRemove, testid }) {
  const [confirm, setConfirm] = useState(false);
  return (
    <div className="flex items-center gap-3 py-2" data-testid={`payout-saved-${testid}`}>
      <Icon className="w-5 h-5 text-[#1e3a5f] shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-xs text-slate-500">{label}</p>
        <p className="font-mono text-sm break-all" data-testid={`payout-saved-${testid}-value`}>{full ? group4(full) : masked}</p>
        {sub && <p className="text-xs text-slate-500 truncate">{sub}</p>}
      </div>
      <button onClick={onReveal} className="p-1.5 text-slate-500" aria-label="Show" data-testid={`payout-saved-${testid}-reveal`}>{full ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
      <button onClick={onEdit} className="text-xs font-semibold text-[#1e3a5f] underline" data-testid={`payout-saved-${testid}-edit`}>Change</button>
      {confirm ? (
        <button onClick={onRemove} onBlur={() => setConfirm(false)} className="text-xs font-semibold text-red-600" data-testid={`payout-saved-${testid}-remove-confirm`}>Remove?</button>
      ) : (
        <button onClick={() => setConfirm(true)} className="p-1.5 text-red-500" aria-label="Remove" data-testid={`payout-saved-${testid}-remove`}><Trash2 className="w-4 h-4" /></button>
      )}
    </div>
  );
}

export default function PayoutDetailsCard({ token }) {
  const cfg = { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} };
  const [pd, setPd] = useState(null);
  const [full, setFull] = useState(null);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({ iban: "", iban_holder: "", paypal_id: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { axios.get(`${API}/member/payout-details`, cfg).then(r => setPd(r.data)).catch(() => setPd({})); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (body) => {
    setBusy(true);
    try {
      const r = await axios.put(`${API}/member/payout-details`, body, cfg);
      setPd(r.data); setFull(null); setShown({}); setEdit(null); setForm({ iban: "", iban_holder: "", paypal_id: "" });
      toast.success("Payout details saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setBusy(false); }
  };
  const [shown, setShown] = useState({});
  const reveal = (k) => async () => {
    if (shown[k]) return setShown({ ...shown, [k]: false });
    try {
      if (!full) { const r = await axios.post(`${API}/member/payout-details/reveal`, {}, cfg); setFull(r.data); }
      setShown({ ...shown, [k]: true });
    } catch { toast.error("Could not show details"); }
  };
  if (!pd) return null;
  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm mb-4" data-testid="payout-details-card">
      <h3 className="font-bold text-[#1e3a5f] mb-1">Payout details</h3>
      <p className="text-xs text-slate-500 mb-3 flex items-start gap-1"><ShieldCheck className="w-3.5 h-3.5 mt-0.5 text-green-600 shrink-0" /> Stored encrypted and shown masked. Used only to pre-fill your expense claims; you can remove them anytime.</p>
      {pd.iban_masked && edit !== "iban" && (
        <SavedRow icon={Landmark} label="Bank account (IBAN)" masked={pd.iban_masked} sub={pd.iban_holder} full={shown.iban ? full?.iban : null} testid="iban"
          onReveal={reveal("iban")} onEdit={() => setEdit("iban")} onRemove={() => save({ iban: "" })} />
      )}
      {pd.paypal_masked && edit !== "paypal" && (
        <SavedRow icon={CreditCard} label="PayPal" masked={pd.paypal_masked} full={shown.paypal ? full?.paypal_id : null} testid="paypal"
          onReveal={reveal("paypal")} onEdit={() => setEdit("paypal")} onRemove={() => save({ paypal_id: "" })} />
      )}
      {(edit === "iban" || !pd.iban_masked) && (
        <div className="space-y-2 pt-2" data-testid="payout-iban-form">
          <p className="text-xs font-semibold text-slate-600">{pd.iban_masked ? "New IBAN" : "Add bank account (IBAN)"}</p>
          <input value={form.iban_holder} onChange={e => setForm({ ...form, iban_holder: e.target.value })} placeholder="Account holder name" className={input} data-testid="payout-holder-input" />
          <input value={form.iban} onChange={e => setForm({ ...form, iban: e.target.value.toUpperCase() })} placeholder="DE89 3704 0044 0532 0130 00" className={`${input} font-mono`} data-testid="payout-details-iban-input" />
          <button disabled={busy || !form.iban} onClick={() => save({ iban: form.iban, iban_holder: form.iban_holder })} className="px-4 py-2 rounded-full bg-[#1e3a5f] text-white text-sm font-semibold disabled:opacity-40" data-testid="payout-iban-save">Save IBAN</button>
        </div>
      )}
      {(edit === "paypal" || !pd.paypal_masked) && (
        <div className="space-y-2 pt-3" data-testid="payout-paypal-form">
          <p className="text-xs font-semibold text-slate-600">{pd.paypal_masked ? "New PayPal ID" : "Add PayPal"}</p>
          <input value={form.paypal_id} onChange={e => setForm({ ...form, paypal_id: e.target.value })} placeholder="paypal@example.com or PayPal.Me/handle" className={input} data-testid="payout-details-paypal-input" />
          <button disabled={busy || !form.paypal_id} onClick={() => save({ paypal_id: form.paypal_id })} className="px-4 py-2 rounded-full bg-[#1e3a5f] text-white text-sm font-semibold disabled:opacity-40" data-testid="payout-paypal-save">Save PayPal</button>
        </div>
      )}
    </div>
  );
}
